import React, { useEffect, useState, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Link, useParams, useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { format } from 'date-fns'
import { Trash2 } from 'lucide-react'

import Button from '../components/Button'
import Input from '../components/Input'
import Select from '../components/Select'
import Loading from '../components/Loading'
import DataTable from '../components/DataTable'
import {
  fetchAttendance,
  getUserByID,
  resetDeviceID,
  resetPassword,
  resignUser,
  updateUser,
} from '../actions/userActions'
import {
  FETCH_ATTENDANCE_RESET,
  GET_USER_BY_ID_RESET,
  RESET_DEVICE_ID_RESET,
  RESET_PASSWORD_RESET,
  RESIGN_USER_RESET,
  UPDATE_USER_RESET,
} from '../constants/userConstants'

const UserByID = () => {
  const navigate = useNavigate();
  let params = useParams()
  let id = params.id.replace(':', '')

  // States
  const [name, setName] = useState('')
  const [deviceInfo, setDeviceInfo] = useState(null)
  const [roleName, setRoleName] = useState('')
  const [email, setEmail] = useState('')
  const [identifier, setIdentifier] = useState('')
  const [ipAddress, setIpAddress] = useState('')
  const [distanceForCalling, setDistanceForCalling] = useState(0)
  const [status, setStatus] = useState(true)
  const [joinedOn, setJoinedOn] = useState('')
  const [resignedOn, setResignedOn] = useState('')
  const [rolesFromServer, setRolesFromServer] = useState([])
  const [roleOptions, setRoleOptions] = useState([])
  const [role, setRole] = useState('')
  const [calling, setCalling] = useState(false)
  const [passwordModal, setPasswordModal] = useState(false)
  const [openResignModal, setOpenResignModal] = useState(false)
  const [showCalling, setShowCalling] = useState(false)
  const [newPassword, setNewPassword] = useState('')
  const [attendance, setAttendance] = useState([])
  const [tasks, setTasks] = useState([])
  const [date, setDate] = useState(format(new Date(), 'yyyy-MM-dd'))

  // Cards
  const [showSettings, setShowSettings] = useState(true)
  const [showAttendance, setShowAttendance] = useState(false)
  const [showTasks, setShowTasks] = useState(false)

  // Roles
  const [taskDetailsOption, setTaskDetailsOption] = useState(false)
  const [updateUserOption, setUpdateUserOption] = useState(false)
  const [loading, setLoading] = useState(true)

  const dispatch = useDispatch()

  // Check for auth
  const userLogin = useSelector((state) => state.userLogin)
  const { userInfo } = userLogin

  useEffect(() => {
    if (!userInfo) {
      navigate('/')
    }
  }, [userInfo, navigate])

  // Check for role
  const getRoleInfo = useSelector((state) => state.getRoleInfo)
  const { getRoleData } = getRoleInfo

  useEffect(() => {
    if (getRoleData) {
      setTaskDetailsOption(getRoleData.tasksDetailsView)
      setUpdateUserOption(getRoleData.usersUpdate)
      if (!getRoleData.usersView) {
        toast('Not Authorized', {
          type: 'error',
          hideProgressBar: true,
          autoClose: 2000,
      })
        navigate('/')
      }
    }
  }, [getRoleData, navigate])

  // On Start
  useEffect(() => {
    dispatch(getUserByID(id))
  }, [dispatch, id])

  const getUserByIDInfo = useSelector((state) => state.getUserByIDInfo)
  const { errorGetUserByIDInfo, getUserByIDData } = getUserByIDInfo

  useEffect(() => {
    dispatch({ type: GET_USER_BY_ID_RESET })
    if (getUserByIDData) {
      setLoading(false)
      // Backend returns user object directly, not nested
      const user = getUserByIDData.user || getUserByIDData
      setName(user.name || '')
      setDeviceInfo(user.deviceInfo || null)
      setEmail(user.email || '')
      setIdentifier(user.identifier || '')
      setIpAddress(user.ipAddress || '')
      setDistanceForCalling(user.distanceForCalling || 0)
      setRoleName(user.role?.name || '')
      setStatus(user.status !== undefined ? user.status : true)
      setJoinedOn(user.joinedOn || '')
      setResignedOn(user.resignedOn || '')
      setRole(user.role?._id || '')
      setRolesFromServer(getUserByIDData.roles || [])
      setCalling(user.calling || false)
      setAttendance(getUserByIDData.attendance || [])
      setTasks(getUserByIDData.tasks || [])
    } else if (errorGetUserByIDInfo) {
      setLoading(false)
      if (errorGetUserByIDInfo.includes('Cast')) {
        navigate('/')
        return
      }
      toast(errorGetUserByIDInfo, {
        type: 'error',
        hideProgressBar: true,
        autoClose: 2000,
      })
    }
  }, [getUserByIDData, errorGetUserByIDInfo, dispatch, navigate])

  // Attendance
  const searchDate = () => {
    if (date) {
      dispatch(fetchAttendance(id, format(new Date(date), 'yyyy-MM-dd')))
    } else {
      toast('Enter a valid date to search', {
        type: 'error',
        hideProgressBar: true,
        autoClose: 2000,
      })
    }
  }

  const fetchAttendanceInfo = useSelector((state) => state.fetchAttendanceInfo)
  const { errorFetchAttendance, fetchAttendanceData } = fetchAttendanceInfo

  useEffect(() => {
    dispatch({ type: FETCH_ATTENDANCE_RESET })
    if (fetchAttendanceData) {
      // Handle attendance data
    } else if (errorFetchAttendance) {
      toast(errorFetchAttendance, {
        type: 'error',
        hideProgressBar: true,
        autoClose: 2000,
      })
    }
  }, [fetchAttendanceData, errorFetchAttendance, dispatch])

  useEffect(() => {
    if (rolesFromServer.length > 0) {
      let data = []
      rolesFromServer.forEach((item) => {
        data.push({ id: item._id, title: item.name })
      })
      setRoleOptions(data)
    }
  }, [rolesFromServer])

  useEffect(() => {
    if (calling === true || calling == 'true') {
      setShowCalling(true)
    } else {
      setShowCalling(false)
    }
  }, [calling])

  // Update User
  const updateUserHandler = () => {
    if (!name || !role || !email || !ipAddress) {
      toast('All the fields are mandatory', {
        type: 'error',
        hideProgressBar: true,
        autoClose: 2000,
      })
      return
    }
    dispatch(
      updateUser(
        id,
        name,
        role,
        Boolean(calling),
        email,
        ipAddress,
        distanceForCalling
      )
    )
  }

  const updateUserInfo = useSelector((state) => state.updateUserInfo)
  const { loadingUpdateUserInfo, errorUpdateUserInfo, updateUserData } = updateUserInfo

  useEffect(() => {
    dispatch({ type: UPDATE_USER_RESET })
    if (updateUserData) {
      toast('User updated successfully', {
        type: 'success',
        hideProgressBar: true,
        autoClose: 2000,
      })
      setTimeout(() => {
        dispatch(getUserByID(id))
      }, 1000)
    } else if (errorUpdateUserInfo) {
      toast(errorUpdateUserInfo, {
        type: 'error',
        hideProgressBar: true,
        autoClose: 2000,
      })
    }
  }, [updateUserData, errorUpdateUserInfo, dispatch, id])

  // Reset Device ID
  const resetDeviceIDHandler = () => {
    dispatch(resetDeviceID(id))
  }

  const resetDeviceIDInfo = useSelector((state) => state.resetDeviceIDInfo)
  const {
    loadingResetDeviceIDInfo,
    errorResetDeviceIDInfo,
    resetDeviceIDData,
  } = resetDeviceIDInfo

  useEffect(() => {
    dispatch({ type: RESET_DEVICE_ID_RESET })
    if (resetDeviceIDData) {
      toast('Device ID reset successfully', {
        type: 'success',
        hideProgressBar: true,
        autoClose: 2000,
      })
    } else if (errorResetDeviceIDInfo) {
      toast(errorResetDeviceIDInfo, {
        type: 'error',
        hideProgressBar: true,
        autoClose: 2000,
      })
    }
  }, [resetDeviceIDData, errorResetDeviceIDInfo, dispatch])

  // Reset Password
  const resetPasswordHandler = () => {
    dispatch(resetPassword(id))
  }

  const resetPasswordInfo = useSelector((state) => state.resetPasswordInfo)
  const {
    loadingResetPasswordInfo,
    errorResetPasswordInfo,
    resetPasswordData,
  } = resetPasswordInfo

  useEffect(() => {
    dispatch({ type: RESET_PASSWORD_RESET })
    if (resetPasswordData) {
      toast('Password reset successful', {
        type: 'success',
        hideProgressBar: true,
        autoClose: 2000,
      })
      setNewPassword(resetPasswordData)
      setPasswordModal(true)
    } else if (errorResetPasswordInfo) {
      toast(errorResetPasswordInfo, {
        type: 'error',
        hideProgressBar: true,
        autoClose: 2000,
      })
    }
  }, [resetPasswordData, errorResetPasswordInfo, dispatch])

  const closePasswordModal = () => {
    setNewPassword('')
    setPasswordModal(false)
  }

  // Resign
  const resignHandler = () => {
    dispatch(resignUser(id))
  }

  const resignUserInfo = useSelector((state) => state.resignUserInfo)
  const { loadingResignUser, errorResignUser, resignUserData } = resignUserInfo

  useEffect(() => {
    dispatch({ type: RESIGN_USER_RESET })
    if (resignUserData) {
      toast(resignUserData.msg, {
        type: 'success',
        hideProgressBar: true,
        autoClose: 2000,
      })
      setOpenResignModal(false)
      setTimeout(() => {
        dispatch(getUserByID(id))
      }, 1000)
    } else if (errorResignUser) {
      toast(errorResignUser, {
        type: 'error',
        hideProgressBar: true,
        autoClose: 2000,
      })
    }
  }, [resignUserData, errorResignUser, dispatch, id])

  // Tasks columns
  const tasksColumns = useMemo(
    () => [
      {
        accessorKey: 'taskID',
        header: 'Task ID',
        cell: ({ row }) =>
          taskDetailsOption ? (
            <Link
              className='font-bold text-md cursor-pointer text-blue-800 bg-blue-100 px-3 py-1 rounded inline-block hover:bg-blue-200 transition-colors'
              target='_blank'
              to={`/tasks/${row.original._id}`}
            >
              {row.original.taskID}
            </Link>
          ) : (
            <div className='font-bold text-md p-2 rounded bg-red-100 text-red-800'>
              <p className='text-sm'>{row.original.taskID}</p>
            </div>
          ),
      },
      {
        accessorKey: 'name',
        header: 'Name',
      },
      {
        accessorKey: 'customer.name',
        header: 'Customer',
      },
      {
        accessorKey: 'type',
        header: 'Type',
      },
      {
        accessorKey: 'status',
        header: 'Status',
      },
      {
        accessorKey: 'allocatedOnFieldOn',
        header: 'Allocated On',
      },
    ],
    [taskDetailsOption]
  )

  // Attendance columns
  const attendanceColumns = useMemo(
    () => [
      {
        accessorKey: 'date',
        header: 'Date',
      },
      {
        accessorKey: 'punchInTime',
        header: 'Punch In Time',
      },
      {
        accessorKey: 'punchInLocation',
        header: 'Punch In Location',
        cell: ({ row }) => row.original.punchInLocation?.address || 'N/A',
      },
      {
        accessorKey: 'punchOutTime',
        header: 'Punch Out Time',
      },
      {
        accessorKey: 'punchOutLocation',
        header: 'Punch Out Location',
        cell: ({ row }) => row.original.punchOutLocation?.address || 'N/A',
      },
    ],
    []
  )

  const processedAttendance = useMemo(() => {
    if (!attendance || attendance.length === 0) return [];

    return attendance.flatMap(day => {
      const punchInsAndOuts = day.punchIns.map((punchIn, index) => ({
        date: day.date,
        punchInTime: punchIn.time,
        punchInLocation: punchIn,
        punchOutTime: day.punchOuts[index] ? day.punchOuts[index].time : 'N/A',
        punchOutLocation: day.punchOuts[index] || { address: 'N/A' },
      }));

      if (day.punchOuts.length > day.punchIns.length) {
        for (let i = day.punchIns.length; i < day.punchOuts.length; i++) {
          punchInsAndOuts.push({
            date: day.date,
            punchInTime: 'N/A',
            punchInLocation: { address: 'N/A' },
            punchOutTime: day.punchOuts[i].time,
            punchOutLocation: day.punchOuts[i],
          });
        }
      }

      return punchInsAndOuts;
    });
  }, [attendance]);

  if (loading) {
    return <Loading />
  }

  return (
    <>
      <div className='w-full h-full'>
        <div className='flex flex-row gap-2 items-end'>
          <h1 className='text-2xl font-semibold'>{name}</h1>
          {deviceInfo && (
            <p className='text-sm mb-1'>
              ({deviceInfo.brand} {deviceInfo.model})
            </p>
          )}
        </div>
        <div className='bg-white shadow-md rounded px-8 py-4 my-4'>
          <div className='flex justify-between'>
            <div className='flex gap-2 md:gap-8 items-center flex-col md:flex-row'>
              <div className='bg-blue-700 h-14 w-52 md:h-24 md:w-96 rounded-full flex flex-col justify-center items-center text-2xl md:text-5xl text-white'>
                {name[0]}
              </div>
              <div className='flex gap-8 items-center w-full md:w-0.5 h-0.5 my-4 md:my-0 md:h-full bg-gray-200'></div>
              <div className='flex flex-col gap-2 w-full items-start'>
                <p className='text-sm'>
                  Email: <span className='font-semibold'>{email}</span>
                </p>
                <p className='text-sm'>
                  Identifier: <span className='font-semibold'>{identifier}</span>
                </p>
              </div>
              <div className='flex gap-8 items-center w-full md:w-0.5 h-0.5 my-4 md:my-0 md:h-full bg-gray-200'></div>
              <div className='flex flex-col gap-2 w-full items-start'>
                <p className='text-sm'>
                  Role: <span className='font-semibold'>{roleName}</span>
                </p>
                <p className='text-sm'>
                  Status:
                  <span className='font-semibold'>
                    {status === true ? 'Active' : 'Not Active'}
                  </span>
                </p>
              </div>
              <div className='flex gap-8 items-center w-full md:w-0.5 h-0.5 my-4 md:my-0 md:h-full bg-gray-200'></div>
              <div className='flex flex-col gap-2 w-full items-start'>
                <p className='text-sm'>
                  Joined On: <span className='font-semibold'>{joinedOn}</span>
                </p>
                <p className='text-sm'>
                  Resigned On:
                  <span className='font-semibold'>
                    {status === true ? 'Active' : resignedOn}
                  </span>
                </p>
              </div>
            </div>
          </div>
        </div>
        <div className='py-2 my-4 flex flex-wrap gap-5'>
          <Button
            custom='py-2 shadow-md bg-black text-white hover:bg-gray-800'
            text='Tasks'
            onClick={() => (
              setShowTasks(true),
              setShowAttendance(false),
              setShowSettings(false)
            )}
          />
          <Button
            custom='py-2 shadow-md bg-black text-white hover:bg-gray-800'
            text='Attendance'
            onClick={() => (
              setShowTasks(false),
              setShowAttendance(true),
              setShowSettings(false)
            )}
          />
          <Button
            custom='py-2 shadow-md bg-black text-white hover:bg-gray-800'
            text='Settings'
            onClick={() => (
              setShowTasks(false),
              setShowAttendance(false),
              setShowSettings(true)
            )}
          />
        </div>

        {showTasks && (
          <DataTable
            data={tasks}
            columns={tasksColumns}
            title="Tasks"
          />
        )}
        {showAttendance && (
          <DataTable
            data={processedAttendance}
            columns={attendanceColumns}
            title="Attendance"
          />
        )}
        {showSettings && (
          <div className='bg-white shadow-md rounded px-8 py-4 my-4'>
            <h3 className='font-semibold text-md'>Settings</h3>
            <div className='flex flex-wrap w-full py-4 gap-5 items-center'>
              <div className='flex flex-col w-1/5'>
                <label className='text-sm mb-2' htmlFor='name'>
                  Name *
                </label>
                <Input
                  placeholder='Name'
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  disabled={!updateUserOption}
                />
              </div>
              <div className='flex flex-col w-1/5'>
                <label className='text-sm mb-2' htmlFor='role'>
                  Role *
                </label>
                <Select
                  width='w-full'
                  name='Role *'
                  value={role}
                  onChange={(e) => setRole(e.target.value)}
                  options={roleOptions}
                  disabled={!updateUserOption}
                />
              </div>
              <div className='flex flex-col w-1/5'>
                <label className='text-sm mb-2' htmlFor='role'>
                  Calling *
                </label>
                <Select
                  width='w-full'
                  name='Calling *'
                  value={calling}
                  onChange={(e) => setCalling(e.target.value)}
                  options={[
                    { id: true, title: 'Active' },
                    { id: false, title: 'Not Active' },
                  ]}
                  disabled={!updateUserOption}
                />
              </div>
              <div className='flex flex-col w-1/5'>
                <label className='text-sm mb-2' htmlFor='role'>
                  Distance for Calling *
                </label>
                <Select
                  width='w-full'
                  name='Distance for Calling *'
                  value={distanceForCalling}
                  onChange={(e) => setDistanceForCalling(e.target.value)}
                  options={[
                    { id: 0.1, title: '100 m' },
                    { id: 0.2, title: '200 m' },
                    { id: 0.3, title: '300 m' },
                    { id: 0.4, title: '400 m' },
                    { id: 0.5, title: '500 m' },
                    { id: 0.6, title: '600 m' },
                    { id: 0.7, title: '700 m' },
                    { id: 0.8, title: '800 m' },
                    { id: 0.9, title: '900 m' },
                    { id: 1.0, title: '1 km' },
                    { id: 100, title: 'No restriction' },
                  ]}
                  disabled={!updateUserOption}
                />
              </div>
              <div className='flex flex-col w-1/5'>
                <label className='text-sm mb-2' htmlFor='email'>
                  Email *
                </label>
                <Input
                  placeholder='Email'
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={!updateUserOption}
                />
              </div>
              <div className='flex flex-col w-1/5'>
                <label className='text-sm mb-2' htmlFor='name'>
                  IP Address *
                </label>
                <Input
                  placeholder='IP Address'
                  value={ipAddress}
                  onChange={(e) => setIpAddress(e.target.value)}
                  disabled={!updateUserOption}
                />
              </div>
            </div>
            <div className='flex flex-wrap w-full gap-5 py-2 items-center'>
            {userInfo.role.settings === true && (
              <Button
                text='Update'
                custom='h-1/2 py-2'
                disabled={!updateUserOption || loadingUpdateUserInfo}
                onClick={updateUserHandler}
              />
            )}
              <Button
                text='Reset Device ID'
                custom='h-1/2 py-2 bg-orange-500 hover:bg-red-700'
                disabled={!updateUserOption || loadingResetDeviceIDInfo}
                onClick={resetDeviceIDHandler}
              />
              <Button
                text='Reset Password'
                custom='h-1/2 py-2 bg-yellow-400 text-black hover:bg-yellow-600 hover:text-white'
                disabled={!updateUserOption || loadingResetPasswordInfo}
                onClick={resetPasswordHandler}
              />
              {userInfo.role.settings === true && (
              <Button
                text='Resign'
                custom='h-1/2 py-2 bg-red-500 text-white hover:bg-red-600 hover:text-white'
                disabled={!updateUserOption || loadingResignUser}
                onClick={() => setOpenResignModal(true)}
              />
              )}
            </div>
          </div>
        )}

        {/* Password Modal */}
        {passwordModal && (
          <>
            <div className='justify-center items-center flex overflow-x-hidden overflow-y-auto fixed inset-0 z-50 outline-none focus:outline-none'>
              <div className='relative w-auto my-6 mx-auto max-w-3xl'>
                <div className='border-0 rounded-lg shadow-lg relative flex flex-col w-full bg-white outline-none focus:outline-none'>
                  <div className='flex items-center justify-between py-3 px-3 border-b border-solid border-blueGray-200 rounded-t'>
                    <h3 className='text-lg font-semibold'>New Password</h3>
                  </div>
                  <div className='relative p-6 pb-2 flex-auto'>
                    <Input
                      width='w-full mb-4'
                      name='New Password *'
                      value={newPassword}
                      disabled
                    />
                  </div>
                  <div className='flex items-center justify-end py-2 border-t border-solid border-blueGray-200 rounded-b'>
                    <button
                      className='text-red-500 background-transparent font-bold uppercase px-6 py-2 text-sm outline-none focus:outline-none mr-1 mb-1 ease-linear transition-all duration-150'
                      type='button'
                      onClick={closePasswordModal}
                    >
                      Close
                    </button>
                  </div>
                </div>
              </div>
            </div>
            <div className='opacity-25 fixed inset-0 z-40 bg-black'></div>
          </>
        )}

        {/* Resign Modal */}
        {openResignModal && (
          <>
            <div className='justify-center items-center flex overflow-x-hidden overflow-y-auto fixed inset-0 z-50 outline-none focus:outline-none'>
              <div className='relative w-auto my-6 mx-auto max-w-3xl'>
                <div className='border-0 rounded-lg shadow-lg relative flex flex-col w-full bg-white outline-none focus:outline-none'>
                  <div className='flex items-center justify-between py-3 px-3 border-b border-solid border-blueGray-200 rounded-t'>
                    <h3 className='text-lg font-semibold'>Resign</h3>
                  </div>
                  <div className='relative p-6 pb-2 flex-auto'>
                    <p className='text-sm mb-3'>
                      Are you sure, you want to resign this employee?
                    </p>
                  </div>
                  <div className='flex items-center justify-end py-2 border-t border-solid border-blueGray-200 rounded-b'>
                    <button
                      className='text-red-500 background-transparent font-bold uppercase px-6 py-2 text-sm outline-none focus:outline-none mr-1 mb-1 ease-linear transition-all duration-150'
                      type='button'
                      onClick={() => setOpenResignModal(false)}
                    >
                      No
                    </button>
                    <button
                      className='bg-emerald-500 text-white active:bg-emerald-600 font-bold uppercase text-sm px-6 py-2 rounded shadow hover:shadow-lg outline-none focus:outline-none mr-1 mb-1 ease-linear transition-all duration-150 disabled:bg-gray-300 mr-4'
                      type='button'
                      onClick={resignHandler}
                      disabled={loadingResignUser}
                    >
                      Yes
                    </button>
                  </div>
                </div>
              </div>
            </div>
            <div className='opacity-25 fixed inset-0 z-40 bg-black'></div>
          </>
        )}
      </div>
    </>
  )
}

export default UserByID
